"use client";

import { useMemo } from "react";
import { Hourglass } from "lucide-react";

import type { PremiumSlice } from "./PremiumPiePanel";

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

/**
 * What is still in underwriting, per sub-agency.
 *
 * Reported production and issued production are both already shown above; the
 * number people actually chase is the gap between them — business written but
 * not yet issued. Deriving it here (reported − issued) avoids another query,
 * and ranking by it answers "where is the money sitting?" directly.
 */
export function UnderwritingPipelinePanel({
  agencyName,
  slices,
  ownPremium = 0,
  ownIssuedPremium = 0,
}: {
  agencyName: string;
  slices: PremiumSlice[];
  ownPremium?: number;
  ownIssuedPremium?: number;
}) {
  const { rows, pendingTotal, reportedTotal } = useMemo(() => {
    const all = [
      ...(ownPremium > 0
        ? [{ id: "__own", name: `${agencyName} (direct)`, premium: ownPremium, issuedPremium: ownIssuedPremium }]
        : []),
      ...slices.filter((s) => s.premium > 0),
    ];
    const mapped = all
      .map((r) => ({
        id: r.id,
        name: r.name,
        pending: Math.max(0, r.premium - (r.issuedPremium || 0)),
        reported: r.premium,
      }))
      .sort((a, b) => b.pending - a.pending);
    return {
      rows: mapped,
      pendingTotal: mapped.reduce((s, r) => s + r.pending, 0),
      reportedTotal: mapped.reduce((s, r) => s + r.reported, 0),
    };
  }, [slices, ownPremium, ownIssuedPremium, agencyName]);

  const pendingShare = reportedTotal > 0 ? (pendingTotal / reportedTotal) * 100 : 0;
  const widest = rows[0]?.pending || 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-50 text-amber-600">
            <Hourglass className="h-3.5 w-3.5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-800">Still in underwriting</p>
            <p className="text-[11px] text-muted-foreground">Reported but not yet issued</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold tabular-nums tracking-tight text-amber-600">
            {money(pendingTotal)}
          </p>
          <p className="text-[11px] tabular-nums text-muted-foreground">
            {pendingShare.toFixed(0)}% of reported
          </p>
        </div>
      </div>

      {pendingTotal <= 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Nothing pending — everything reported has issued.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id}>
              <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-gray-700">{r.name}</span>
                <span className="shrink-0 font-medium tabular-nums text-gray-900">
                  {money(r.pending)}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-amber-500"
                  style={{ width: `${widest > 0 ? (r.pending / widest) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
